import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCopyToClipboard } from "@/components/copy-to-clipboard";
import { SearchBar } from "@/components/search-bar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SummonRoomSystemForm } from "@/features/summon-room-system/components/summon-room-system-form";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { cn } from "@/lib/utils";
import { FocusScope } from "@radix-ui/react-focus-scope";

import { useIsModerator } from "../api/is-moderator";

import { ParticipantsCounter } from "./participants-counter";
import { ParticipantsList } from "./participants-list";
import { RaisedHandList } from "./raised-hand-list";
import { ToggleAllCamera } from "./toggle-all-camera";
import { ToggleAllMic } from "./toggle-all-mic";

export function ParticipantsDrawer() {
  const isOpen = useVidyoStore((s) => s.participantsDrawer.isOpen);
  const closeParticipantsDrawer = useVidyoStore(
    (s) => s.participantsDrawer.close,
  );
  const { copied, copyToClipboard } = useCopyToClipboard(800);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const participants = useVidyoStore((s) => s.participants.participants);
  const isModerator = useIsModerator();

  // Close settings drawer when escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeParticipantsDrawer();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeParticipantsDrawer]);

  // Close settings drawer when component unmounts
  useEffect(() => {
    return () => {
      closeParticipantsDrawer();
    };
  }, [closeParticipantsDrawer]);

  const [search, setSearch] = useState<string | null>(null);
  const filteredParticipants = participants.filter((p) =>
    p.name.toLowerCase().includes(search?.toLowerCase() ?? ""),
  );

  return (
    <FocusScope
      trapped={isOpen}
      loop
      onMountAutoFocus={(e) => {
        e.preventDefault();
      }}
      onUnmountAutoFocus={(e) => {
        e.preventDefault();
      }}
      asChild
    >
      <motion.div
        className={cn(
          "fixed bottom-[var(--action-bar-height)] right-3 top-4 z-10 h-auto w-80",
          "rounded-md border border-border bg-background ring-4 ring-card",
        )}
        initial={{ x: 320 + 12 }}
        animate={{ x: isOpen ? 0 : 320 + 16 }} // 16 is the right-4 on this component
        transition={{ duration: 0.4, ease: "easeInOut" }}
        role="dialog"
        aria-hidden={!isOpen}
        aria-labelledby="participants-drawer-title"
        //@ts-expect-error inert is supported only react 19+
        inert={!isOpen ? "" : undefined}
      >
        <div className="flex h-full flex-col" ref={contentContainerRef}>
          <p
            className="px-4 py-4 text-sm text-foreground"
            id="participants-drawer-title"
          >
            People
          </p>

          <Button
            variant="ghost"
            size="icon"
            onClick={closeParticipantsDrawer}
            className="absolute right-2 top-2"
          >
            <X className="size-4" />
          </Button>
          <Separator className="bg-border" />

          <ScrollArea
            className={`h-[${contentContainerRef.current?.clientHeight}px]`}
          >
            <div className="flex flex-col">
              <TooltipProvider>
                <div className="flex flex-col p-2">
                  <div className="mb-2 flex justify-center">
                    <ToggleAllMic />

                    <ToggleAllCamera />
                  </div>
                  <div className="flex flex-row gap-2">
                    <SearchBar
                      className="w-full sm:w-full"
                      search={search}
                      setSearch={setSearch}
                      placeholder="Search participants"
                    />
                    {isModerator && <SummonRoomSystemForm />}
                  </div>
                </div>
              </TooltipProvider>
              <div>
                <RaisedHandList search={search} />
              </div>
              <div className="m-2">
                <p className="px-4 py-3 text-sm text-foreground">
                  {!search && "All"} Participants{" "}
                  <ParticipantsCounter count={participants.length} />
                </p>
                <ParticipantsList participants={filteredParticipants} />
              </div>
            </div>
          </ScrollArea>
          <div className="mt-auto p-4 text-center">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(window.location.href)}
              className="min-w-[155px]"
            >
              <AnimatePresence mode="wait">
                {!copied ? (
                  <motion.span
                    key="copy-text"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-2"
                  >
                    <span>Copy Call Link</span>
                    <Copy className="size-4" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="copied-text"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-2"
                  >
                    <span>Link Copied</span>
                    <Check className="size-4" />
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </div>
      </motion.div>
    </FocusScope>
  );
}
